<template>
  <v-card class="pa-6" outlined>
    <h3 class="black--text mb-1">{{ $t("reports.aiCredit.usageLogTitle") }}</h3>
    <p class="grey--text mb-4" style="max-width: 1020px">
      {{ $t("reports.aiCredit.usageLogHelp") }}
    </p>

    <!-- Filters + pagination -->
    <div class="d-flex flex-wrap align-end justify-space-between mb-3">
      <div class="d-flex flex-wrap align-end" style="gap: 16px; flex: 1 1 auto">
        <v-text-field
          v-model="search"
          :placeholder="$t('reports.aiCredit.search')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          style="max-width: 340px"
        />
        <div style="max-width: 300px; width: 100%">
          <div class="grey--text text-caption mb-1">
            {{ $t("reports.aiCredit.filterCooperative") }}
          </div>
          <v-select
            v-model="organization"
            :items="cooperativeOptions"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </div>
        <div style="max-width: 300px; width: 100%">
          <div class="grey--text text-caption mb-1">
            {{ $t("reports.aiCredit.filterAction") }}
          </div>
          <v-select
            v-model="action"
            :items="actionOptions"
            :placeholder="$t('reports.aiCredit.makeSelection')"
            outlined
            dense
            hide-details
            clearable
          />
        </div>
      </div>
      <div class="d-flex align-center">
        <span class="grey--text mr-2">{{ rangeLabel }}</span>
        <v-btn
          icon
          outlined
          color="primary"
          class="mr-1"
          :disabled="page <= 1"
          @click="prevPage"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          icon
          outlined
          color="primary"
          :disabled="!hasNextPage"
          @click="nextPage"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      hide-default-footer
      class="elevation-0 usage-table"
    >
      <template v-slot:[`item.dateTime`]="{ item }">{{
        fmtDateTime(item.dateTime)
      }}</template>
      <template v-slot:[`item.cooperative`]="{ item }">
        <div class="font-weight-medium black--text">
          {{ item.cooperative || "-" }}
        </div>
        <div class="grey--text text-caption" v-if="item.country">
          {{ item.country }}
        </div>
      </template>
      <template v-slot:[`item.action`]="{ item }">{{
        actionLabel(item.action)
      }}</template>
      <template v-slot:[`item.change`]="{ item }">
        <span
          :class="item.change < 0 ? 'red--text' : 'green--text text--darken-1'"
        >
          {{ item.change > 0 ? "+" : "" }}{{ formatNum(item.change) }}
          {{ $t("reports.aiCredit.credits") }}
        </span>
      </template>
      <template v-slot:[`item.newBalance`]="{ item }">
        {{ formatNum(item.newBalance) }} {{ $t("reports.aiCredit.credits") }}
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";

const ACTIONS = [
  "allocation",
  "recurring_allocation",
  "top_up",
  "reservation",
  "reservation_release",
  "report_usage",
  "adjustment",
];

export default {
  name: "UsageLog",
  mixins: [creditFormat],
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      limit: 10,
      search: "",
      organization: null,
      action: null,
      loading: false,
      searchTimer: null,
      cooperativeOptions: [],
    };
  },
  computed: {
    headers() {
      const t = (k) => this.$t(`reports.aiCredit.${k}`);
      return [
        { text: t("colDateTime"), value: "dateTime", sortable: false },
        { text: t("colCooperative"), value: "cooperative", sortable: false },
        { text: t("colAction"), value: "action", sortable: false },
        { text: t("colDescription"), value: "description", sortable: false },
        { text: t("colChange"), value: "change", sortable: false },
        { text: t("colNewBalance"), value: "newBalance", sortable: false },
      ];
    },
    actionOptions() {
      return ACTIONS.map((a) => ({ text: this.actionLabel(a), value: a }));
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetch();
      }, 500);
    },
    organization() {
      this.page = 1;
      this.fetch();
    },
    action() {
      this.page = 1;
      this.fetch();
    },
  },
  created() {
    this.fetch();
    this.loadCooperatives();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.search) params.search = this.search;
        if (this.organization) params.organization = this.organization;
        if (this.action) params.action = this.action;
        const env = await AiCreditService.usageLog(params);
        const payload = (env && env.data) || {};
        this.rows = payload.logs || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCooperatives() {
      try {
        const env = await AiCreditService.listCooperatives({
          page: 1,
          limit: 200,
        });
        const list = (env && env.data && env.data.cooperatives) || [];
        this.cooperativeOptions = list.map((c) => ({
          text: c.cooperative,
          value: c.organization,
        }));
      } catch (e) {
        // filter is optional; leave empty on failure
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    actionLabel(action) {
      const key = `reports.aiCredit.action_${action}`;
      const label = this.$t(key);
      return label === key ? action : label;
    },
  },
};
</script>

<style scoped>
.usage-table >>> thead th {
  background: #1b7a43;
  color: #fff !important;
  font-weight: 600;
}
</style>
